import { usesWordSegmenter } from "helpers/utils";
import { countWords } from "pages/media/utils";
import { CANTONESE_WORDS } from "./cantoneseWords";
import type {
  AnchorTag,
  Marker,
  ParsedScript,
  AnchorRange,
  AnchorPosition,
  Segment,
  WordSpan,
} from "./types";

/**
 * 1. Add the tag to the `AnchorTag` type type (e.g., `"TAG"`)
 * 2. Add it to the `VALID_TAGS` set
 * 3. Update `renderRangeAsHtml` and `domToParsedScript` in apps/admin/src/pages/media/components/storyboard/editor/helpers.ts if the new anchor
 *    needs to survive in the contentEditable DOM
 * 4. Update `parsedToWordTokens` / `wordTokensToScript` if the new anchor
 *    needs to be reflected in word-level editing
 */

const VALID_TAGS = new Set<string>(["HL", "EMO", "PRN", "SLIDE"]);
/**
 * Matches any anchor: [[TAG-id]] or [[/TAG-id]].
 * Mirrors the backend's GENERIC_ANCHOR_REGEX.
 */
const ANCHOR_REGEX =
  /\[\[(?<closingMarker>\/?)(?<tag>[A-Za-z]+)-(?<id>[^[\]]*)\]\]/g;

/**
 * Parse a bracket-notation script into plain text + position-based markers.
 * Only markers with valid tags (HL, EMO, PRN, SLIDE) are extracted.
 *
 * SLIDE tags represent slide boundaries. When stripping them would merge
 * adjacent words (e.g. `century.[[SLIDE-x]]Born` → `century.Born`), a space
 * is injected so downstream word-splitting sees two tokens.
 */
export const parseScript = (input: string): ParsedScript => {
  if (!input) return { text: "", markers: [] };

  const markers: Marker[] = [];
  const textParts: string[] = [];
  let lastIndex = 0;
  let offset = 0;

  ANCHOR_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = ANCHOR_REGEX.exec(input)) !== null && match.groups) {
    const closingMarker = match.groups.closingMarker;
    const tag = match.groups.tag?.toUpperCase();
    const id = match.groups.id;
    const anchorStart = match.index;
    const anchorEnd = match.index + match[0].length;

    if (tag && id && VALID_TAGS.has(tag)) {
      textParts.push(input.slice(lastIndex, anchorStart));

      // SLIDE tags act as slide boundaries — inject a space when stripping
      // would merge adjacent words (no whitespace on either side).
      let spaceInjected = false;
      if (tag === "SLIDE") {
        const lastChar = lastCharOf(textParts);
        const nextChar = input[anchorEnd];
        if (
          lastChar &&
          !/\s/.test(lastChar) &&
          nextChar &&
          !/\s/.test(nextChar)
        ) {
          textParts.push(" ");
          spaceInjected = true;
        }
      }

      markers.push({
        raw: match[0],
        tag: tag as AnchorTag,
        id,
        position: anchorStart - offset + (spaceInjected ? 1 : 0),
        closing: closingMarker === "/",
      });
      offset += anchorEnd - anchorStart - (spaceInjected ? 1 : 0);
      lastIndex = anchorEnd;
    }
  }

  textParts.push(input.slice(lastIndex));
  markers.sort(anchorSortComparator);

  return { text: textParts.join(""), markers };
};

/** Last non-empty character across all accumulated text parts. */
const lastCharOf = (parts: string[]): string => {
  for (let i = parts.length - 1; i >= 0; i--) {
    if ((parts[i]?.length ?? 0) > 0) return parts[i]?.at(-1) ?? "";
  }
  return "";
};

/**
 * Remove all anchors with the given tag from a bracket-notation script,
 * preserving the surrounding text and other anchors.
 */
export const stripHighlightAnchors = (script: string): string => {
  if (!script) return script;
  const parsed = parseScript(script);
  return serializeScript({
    text: parsed.text,
    markers: parsed.markers.filter((m) => m.tag !== "HL"),
  });
};

/**
 * Serialize a ParsedScript back to bracket notation.
 * At the same position, closing markers come before opening markers
 * (matching the backend's `_reinsert_anchors`).
 */
export const serializeScript = (parsed: ParsedScript): string => {
  const { text, markers } = parsed;
  if (markers.length === 0) return text;

  const sorted = [...markers].sort(anchorSortComparator);
  const parts: string[] = [];
  let textIndex = 0;

  for (const marker of sorted) {
    const pos = Math.max(0, Math.min(marker.position, text.length));
    parts.push(text.slice(textIndex, pos), marker.raw);
    textIndex = pos;
  }

  parts.push(text.slice(textIndex));
  return parts.join("");
};

/**
 * Sort: position asc → closing before opening → alphabetical by stripped raw.
 * Matches the backend's `_reinsert_anchors` sort key:
 * `(0 if closing else 1, anchor.strip("[]"))`
 */
export const anchorSortComparator = (a: Marker, b: Marker): number => {
  if (a.position !== b.position) return a.position - b.position;
  const aRank = a.closing ? 0 : 1;
  const bRank = b.closing ? 0 : 1;
  if (aRank !== bRank) return aRank - bRank;
  return a.raw
    .replaceAll(/[[\]]/g, "")
    .localeCompare(b.raw.replaceAll(/[[\]]/g, ""));
};

/**
 * Pairs opening markers with their matching closing markers.
 * Unpaired markers are returned with `orphan: "start"` or `"end"`.
 */
export const getAnchorRanges = (
  parsed: ParsedScript,
  tag: "HL" | "PRN"
): AnchorRange[] => {
  const { text, markers } = parsed;
  const result: AnchorRange[] = [];
  const matched = new Set<Marker>();

  for (const opening of markers.filter((m) => m.tag === tag && !m.closing)) {
    const closing = markers.find(
      (m) =>
        m.tag === tag && m.closing && m.id === opening.id && !matched.has(m)
    );

    if (closing) {
      matched.add(closing);
      result.push({
        id: opening.id,
        start: opening.position,
        end: closing.position,
        text: text.slice(opening.position, closing.position),
      });
    } else {
      result.push({
        id: opening.id,
        start: opening.position,
        end: text.length,
        text: text.slice(opening.position),
        orphan: "start",
      });
    }
  }

  // Orphan closings
  for (const m of markers) {
    if (m.tag === tag && m.closing && !matched.has(m)) {
      result.push({
        id: m.id,
        start: 0,
        end: m.position,
        text: text.slice(0, m.position),
        orphan: "end",
      });
    }
  }

  return result.sort((a, b) => a.start - b.start);
};

/** Get positions for single anchors (EMO, SLIDE). */
export const getAnchorPositions = (
  parsed: ParsedScript,
  tag: "EMO" | "SLIDE"
): AnchorPosition[] => {
  return parsed.markers
    .filter((m) => m.tag === tag && !m.closing)
    .map((m) => ({ id: m.id, position: m.position }));
};

/** Drop anchors by tag type(s), keep the text unchanged. */
export const removeAnchorsByTag = (
  parsed: ParsedScript,
  ...tags: AnchorTag[]
): ParsedScript => {
  const tagSet = new Set(tags);
  return {
    text: parsed.text,
    markers: parsed.markers.filter((m) => !tagSet.has(m.tag)),
  };
};

/** Remove a specific highlight by its id (HL-xxx) from a bracket-notation string. */
export const removeHighlightById = (input: string, hlId: string): string => {
  if (!input || !hlId) return input;
  const parsed = parseScript(input);
  return serializeScript({
    text: parsed.text,
    markers: parsed.markers.filter((m) => !(m.tag === "HL" && m.id === hlId)),
  });
};

/** Strip HL anchors whose id is not in the provided set (orphan anchors). */
export const stripOrphanHighlightAnchors = (
  input: string,
  validHighlightIds: Set<string>
): string => {
  if (!input) return input;
  const parsed = parseScript(input);
  const orphanIds = Array.from(
    new Set(
      parsed.markers
        .filter((m) => m.tag === "HL" && !validHighlightIds.has(m.id))
        .map((m) => m.id)
    )
  );
  if (orphanIds.length === 0) return input;
  return orphanIds.reduce((acc, id) => removeHighlightById(acc, id), input);
};

const getBaseLanguage = (language?: string) =>
  language?.toLowerCase().split("-")[0] ?? "";

/**
 * Character spans of each word in plain text (with all markers stripped).
 * Chinese/Cantonese are split by dictionary words; every other language
 * (or no language) splits on whitespace.
 */
export const getWordSpans = (text: string, language?: string): WordSpan[] =>
  usesWordSegmenter(language) && typeof Intl.Segmenter === "function"
    ? // Intl.Segmenter throws on "zh_CN"; it needs a BCP 47 tag ("zh-CN").
      getSegmentedWordSpans(text, (language ?? "").replace(/_/g, "-"))
    : getWhitespaceWordSpans(text);

const getWhitespaceWordSpans = (text: string): WordSpan[] => {
  const spans: WordSpan[] = [];
  let charPos = 0;
  for (const raw of text.split(/\s+/).filter(Boolean)) {
    while (charPos < text.length && /\s/.test(text.charAt(charPos))) charPos++;
    spans.push({ start: charPos, end: charPos + raw.length, raw });
    charPos += raw.length;
  }
  return spans;
};

/** Opening brackets/quotes and prefix symbols (#FF43AF, @name, $100). */
const LEADING_SYMBOLS = new Set("「『（《〈【〔“‘([{#@$¥￥€£");
const CANTONESE_MAX_WORD_LENGTH = Math.max(
  ...[...CANTONESE_WORDS].map((word) => word.length)
);

// Segmenter construction is slow and getWordSpans runs on every index lookup.
const segmenters = new Map<string, Intl.Segmenter>();
const getSegmenter = (language: string) => {
  let segmenter = segmenters.get(language);
  if (!segmenter) {
    segmenter = new Intl.Segmenter(language, { granularity: "word" });
    segmenters.set(language, segmenter);
  }
  return segmenter;
};

/** Merge adjacent word segments that form a known Cantonese word (我|哋 → 我哋). */
const mergeCantoneseWords = (text: string, segments: Segment[]): Segment[] => {
  const merged: Segment[] = [];
  let i = 0;
  while (i < segments.length) {
    // Each segment is at least one character, so no word spans more segments.
    const window = segments.slice(i, i + CANTONESE_MAX_WORD_LENGTH);
    const [first] = window;
    if (!first) break;
    let match = [first];
    // Longest match first.
    for (let size = window.length; size > 1; size--) {
      const pieces = window.slice(0, size);
      const word = pieces.map((s) => text.slice(s.start, s.end)).join("");
      if (pieces.every((s) => s.isWordLike) && CANTONESE_WORDS.has(word)) {
        match = pieces;
        break;
      }
    }
    merged.push(match.reduce((acc, s) => ({ ...acc, end: s.end }), first));
    i += match.length;
  }
  return merged;
};

/**
 * Dictionary-based word spans. Punctuation sticks to the previous word and
 * LEADING_SYMBOLS to the next one, like "word," in whitespace splitting:
 * text outside spans is dropped by parsedToWordTokens.
 */
const getSegmentedWordSpans = (text: string, language: string): WordSpan[] => {
  let segments: Segment[] = [...getSegmenter(language).segment(text)].map(
    ({ segment, index, isWordLike }) => ({
      start: index,
      end: index + segment.length,
      isWordLike: !!isWordLike,
    })
  );
  if (getBaseLanguage(language) === "yue") {
    segments = mergeCantoneseWords(text, segments);
  }

  const spans: WordSpan[] = [];
  // Leading symbols waiting for the word they belong to.
  let pending: { start: number; end: number } | undefined;
  let afterWhitespace = true;
  for (const { start, end, isWordLike } of segments) {
    const raw = text.slice(start, end);
    if (/^\s+$/.test(raw)) {
      afterWhitespace = true;
      continue;
    }
    if (!isWordLike && LEADING_SYMBOLS.has(raw.charAt(0))) {
      pending = { start: pending?.start ?? start, end };
      continue;
    }
    const last = spans.at(-1);
    if (isWordLike || pending || afterWhitespace || !last) {
      const spanStart = pending?.start ?? start;
      spans.push({ start: spanStart, end, raw: text.slice(spanStart, end) });
      pending = undefined;
    } else {
      last.end = end;
      last.raw = text.slice(last.start, end);
    }
    afterWhitespace = false;
  }
  if (pending) {
    spans.push({ ...pending, raw: text.slice(pending.start, pending.end) });
  }
  return spans;
};

export const countScriptWords = (str: string) => {
  const { text } = parseScript(str);
  return countWords(text);
};
